import { Check, Flame, TrendingUp, ChevronRight, AlertCircle, Loader } from "lucide-react";
import { Logo } from "@/components/logo";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/app-context";
import { toDateStr, DAYS, DAY_LABELS, getActiveWeekSchedule } from "@/lib/helpers";

export function Home() {
  const navigate = useNavigate();
  const { state, dispatch, insights, sheetsStatus } = useApp();

  const today = toDateStr(new Date());
  const dayIdx = new Date().getDay();
  const dayName = DAYS[dayIdx === 0 ? 6 : dayIdx - 1];
  const dayLabel = DAY_LABELS[dayName];

  const activeSchedule = getActiveWeekSchedule(state);
  const todaySchedule = activeSchedule.find((d) => d.day === dayName);
  const override = state.weeklyOverrides[today];
  const todayWorkoutId = override || todaySchedule?.workoutId;
  const todayWorkout = todayWorkoutId ? state.workouts[todayWorkoutId] : null;
  const alreadyTracked = state.history.some((h) => h.date === today);

  const streak = insights?.streak || { current: 0, best: 0 };
  const consistency = insights?.consistency || { score: 0, completed: 0, plannedPerWeek: 0 };
  const volumeTrend = insights?.volumeTrend || { weeklyVolumes: [] };

  // Calculate weekly stats
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - dayIdx + 1); // Monday
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6); // Sunday

  const weekStartStr = toDateStr(weekStart);
  const weekEndStr = toDateStr(weekEnd);

  const thisWeekWorkouts = state.history.filter(
    (h) => h.date >= weekStartStr && h.date <= weekEndStr
  );
  const weekVolume = thisWeekWorkouts.reduce((sum, h) => {
    const vol = (h.exercises || []).reduce((exSum, ex) => {
      const exVol = (ex.sets || []).reduce((setSum, s) => {
        if (!s.warmup && s.weight > 0 && s.reps > 0) {
          return setSum + s.weight * s.reps;
        }
        return setSum;
      }, 0);
      return exSum + exVol;
    }, 0);
    return sum + vol;
  }, 0);

  // Estimate RIR from recent sessions
  let recentRIR = 0.9;
  if (thisWeekWorkouts.length > 0) {
    const latestWorkout = thisWeekWorkouts[thisWeekWorkouts.length - 1];
    const rirs = (latestWorkout.exercises || [])
      .flatMap((ex) => (ex.sets || []).filter((s) => s.rir !== undefined).map((s) => s.rir || 0))
      .filter((r) => r > 0);
    if (rirs.length > 0) {
      recentRIR = rirs.reduce((a, b) => a + b, 0) / rirs.length;
    }
  }

  // Day status for the week
  const weekDays = DAYS.slice(0, 7);
  const workoutStatus = weekDays.map((dn) => {
    const dateOffset = weekDays.indexOf(dn);
    const checkDate = new Date(weekStart);
    checkDate.setDate(checkDate.getDate() + dateOffset);
    const checkDateStr = toDateStr(checkDate);
    return state.history.some((h) => h.date === checkDateStr);
  });

  // Get scheduled workout label for a given day (for future untracked days)
  const getDayLabel = (dn: string, index: number): string => {
    const slot = state.weeklySchedule.find((s) => s.day === dn);
    if (slot?.workoutId && state.workouts[slot.workoutId]) {
      return state.workouts[slot.workoutId].name;
    }
    return slot?.workoutId === null ? "Rest" : "";
  };

  // Recent workouts from history
  const recentWorkouts = thisWeekWorkouts.slice(-2).reverse().map((h) => {
    const totalSets = (h.exercises || []).reduce((sum, ex) => sum + (ex.sets || []).length, 0);
    const totalVolume = (h.exercises || []).reduce((sum, ex) => {
      return (
        sum +
        (ex.sets || []).reduce((exSum, s) => {
          if (!s.warmup && s.weight > 0 && s.reps > 0) {
            return exSum + s.weight * s.reps;
          }
          return exSum;
        }, 0)
      );
    }, 0);

    // Calculate avg RIR in this workout
    const rirs = (h.exercises || [])
      .flatMap((ex) => (ex.sets || []).filter((s) => !s.warmup && s.rir !== undefined).map((s) => s.rir || 0))
      .filter((r) => r > 0);
    const avgRIR = rirs.length > 0 ? (rirs.reduce((a, b) => a + b, 0) / rirs.length) : 0;

    // Build deterministic set volume bars for the mini chart
    const setBars = (h.exercises || []).flatMap((ex) =>
      (ex.sets || [])
        .filter((s) => !s.warmup && s.weight > 0 && s.reps > 0)
        .map((s) => s.weight * s.reps)
    );
    const maxBar = Math.max(...setBars, 1);
    const normalizedBars = setBars.map((v) => Math.max(20, (v / maxBar) * 100));

    return {
      name: h.workoutName,
      focus: (h.exercises && h.exercises[0]?.name) || "Workout",
      sets: totalSets,
      volume: totalVolume > 1000 ? `${(totalVolume / 1000).toFixed(1)}k` : `${totalVolume}`,
      intensity: avgRIR < 1 ? "HARD" : avgRIR < 2 ? "MODERATE" : "LIGHT",
      hasPR: false,
      delta: "+2.5%",
      isNew: false,
      bars: normalizedBars,
    };
  });

  // Build volume trend bars for insights chart from real weekly volumes
  const weeklyVolumes = volumeTrend.weeklyVolumes || [];
  const maxWeeklyVol = Math.max(...weeklyVolumes, 1);
  const volumeBars = weeklyVolumes.map((v) => Math.max(5, (v / maxWeeklyVol) * 100));

  // Sync badge logic
  const getSyncBadgeStyle = () => {
    if (!state.gapiToken) {
      return { bgColor: "#3F3F46", textColor: "#A1A1AA", icon: null, label: "Offline" };
    }
    if (sheetsStatus === 'syncing') {
      return { bgColor: "#78350F", textColor: "#FCD34D", icon: Loader, label: "Syncing..." };
    }
    if (sheetsStatus === 'error') {
      return { bgColor: "#7F1D1D", textColor: "#FCA5A5", icon: AlertCircle, label: "Sync Error" };
    }
    if (sheetsStatus === 'paused') {
      return { bgColor: "#3F3F46", textColor: "#A1A1AA", icon: null, label: "Offline" };
    }
    return { bgColor: "#1F4428", textColor: "#4ADE80", icon: Check, label: "Synced" };
  };

  const badgeStyle = getSyncBadgeStyle();
  const BadgeIcon = badgeStyle.icon;

  return (
    <div className="p-5 pb-24" style={{ backgroundColor: "#18181B" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5" style={{ height: "32px" }}>
        <Logo />
        <div
          className="px-2.5 py-1 rounded-full flex items-center gap-1"
          style={{ backgroundColor: badgeStyle.bgColor, color: badgeStyle.textColor, fontSize: "11px", fontWeight: 600 }}
        >
          {BadgeIcon && <BadgeIcon size={10} />}
          <span>{badgeStyle.label}</span>
        </div>
      </div>

      {/* THIS WEEK Section */}
      <div className="flex items-center justify-between mb-4">
        <span style={{ color: "#F0F0F0", fontSize: "13px", fontWeight: 600, letterSpacing: "0.5px" }}>
          THIS WEEK
        </span>
      </div>

      {/* Progress Ring and Stats */}
      <div className="flex items-center gap-6 mb-6">
        {/* Circular Progress */}
        <div className="relative flex items-center justify-center" style={{ width: "100px", height: "100px" }}>
          <svg className="transform -rotate-90" width="100" height="100">
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke="#27272A"
              strokeWidth="8"
            />
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke="url(#gradient)"
              strokeWidth="8"
              strokeDasharray={`${(thisWeekWorkouts.length / (consistency.plannedPerWeek || 1)) * 264} 264`}
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFA033" />
                <stop offset="50%" stopColor="#FF6B35" />
                <stop offset="100%" stopColor="#E8432A" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span style={{ color: "#F0F0F0", fontSize: "28px", fontWeight: 700 }}>
              {thisWeekWorkouts.length}/{consistency.plannedPerWeek}
            </span>
            <span style={{ color: "#71717A", fontSize: "11px" }}>sessions</span>
          </div>
        </div>

        {/* Stats */}
        <div className="flex flex-col gap-4 justify-center">
          <div>
            <div className="flex items-baseline gap-1 mb-0.5">
              <span style={{ color: "#F0F0F0", fontSize: "20px", fontWeight: 700 }}>
                {state.history.length === 0 ? "—" : weekVolume > 1000 ? `${(weekVolume / 1000).toFixed(1)}k` : weekVolume}
              </span>
            </div>
            <span style={{ color: "#71717A", fontSize: "12px" }}>Volume</span>
          </div>
          <div>
            <div className="flex items-baseline gap-1 mb-0.5">
              <span style={{ color: "#FF6B35", fontSize: "20px", fontWeight: 700 }}>
                {state.history.length === 0 ? "—" : recentRIR.toFixed(1)}
              </span>
            </div>
            <div style={{ color: "#71717A", fontSize: "12px" }}>
              HARD RIR
            </div>
          </div>
        </div>
      </div>

      {/* Week Strip */}
      <div
        className="p-3 mb-5"
        style={{
          backgroundColor: "#27272A",
          borderRadius: "16px",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <span style={{ color: "#71717A", fontSize: "11px", fontWeight: 600 }}>ACTIVITY</span>
          <Check size={14} style={{ color: "#4ADE80" }} />
        </div>
        <div className="grid grid-cols-7 gap-2">
          {DAYS.slice(0, 7).map((day, index) => (
            <div key={day} className="flex flex-col items-center gap-1.5">
              <span style={{ color: "#71717A", fontSize: "11px" }}>{DAY_LABELS[day]}</span>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{
                  backgroundColor: workoutStatus[index] ? "#FF6B35" : "#3F3F46",
                }}
              >
                {workoutStatus[index] ? (
                  <Check size={16} style={{ color: "#18181B" }} />
                ) : (
                  <span style={{ color: "#71717A", fontSize: "13px", fontWeight: 600 }}>
                    {getDayLabel(day, index)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Today's Workout Card or Rest Day Card */}
      {todayWorkout ? (
        <div
          className="p-4 mb-5"
          style={{
            backgroundColor: "#27272A",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span style={{ color: "#F0F0F0", fontSize: "15px", fontWeight: 600 }}>
              {todayWorkout.name}
            </span>
            <span style={{ color: "#71717A", fontSize: "13px", fontWeight: 600 }}>
              {todayWorkout.exercises.length} exercises
            </span>
          </div>
          <p style={{ color: "#71717A", fontSize: "13px", marginBottom: "12px" }}>
            {todayWorkout.exercises.map((e) => e.name).join(", ")}
          </p>
          {!alreadyTracked ? (
            <button
              className="w-full rounded-lg flex items-center justify-center gap-2"
              style={{
                backgroundColor: "#FF6B35",
                color: "#18181B",
                padding: "12px",
                fontSize: "14px",
                fontWeight: 700,
              }}
              onClick={() => {
                dispatch({
                  type: "START_TRACKING",
                  session: {
                    workoutId: todayWorkout.id,
                    exercises: todayWorkout.exercises,
                    startTime: Date.now(),
                  },
                });
                navigate("/track-workout");
              }}
            >
              Start Workout
            </button>
          ) : (
            <div
              className="w-full rounded-lg flex items-center justify-center"
              style={{
                backgroundColor: "#1F4428",
                color: "#4ADE80",
                padding: "12px",
                fontSize: "14px",
                fontWeight: 700,
              }}
            >
              Completed today
            </div>
          )}
        </div>
      ) : (
        <div
          className="p-4 mb-5"
          style={{
            backgroundColor: "#27272A",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span style={{ color: "#F0F0F0", fontSize: "15px", fontWeight: 600 }}>Rest day</span>
            <span style={{ color: "#71717A", fontSize: "13px", fontWeight: 600 }}>Recovery</span>
          </div>
          <p style={{ color: "#71717A", fontSize: "13px", marginBottom: "12px" }}>
            Prioritize sleep and recovery
          </p>
          <button
            className="w-full rounded-lg flex items-center justify-center gap-2"
            style={{
              backgroundColor: "#FF6B35",
              color: "#18181B",
              padding: "12px",
              fontSize: "14px",
              fontWeight: 700,
            }}
            onClick={() => navigate("/track-workout")}
          >
            Track a Workout Anyway
          </button>
        </div>
      )}

      {/* Recent Workouts or Empty State */}
      {state.history.length === 0 ? (
        <div
          className="p-6 mb-5 text-center"
          style={{
            backgroundColor: "#27272A",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div style={{ color: "#F0F0F0", fontSize: "16px", fontWeight: 600, marginBottom: "8px" }}>
            Ready to start?
          </div>
          <div style={{ color: "#71717A", fontSize: "13px", marginBottom: "16px" }}>
            Create your first workout to begin tracking your progress.
          </div>
          <button
            onClick={() => navigate("/create-workout")}
            className="w-full rounded-lg"
            style={{
              backgroundColor: "#FF6B35",
              color: "#18181B",
              padding: "12px",
              fontSize: "14px",
              fontWeight: 700,
            }}
          >
            Create First Workout
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <span style={{ color: "#F0F0F0", fontSize: "13px", fontWeight: 600, letterSpacing: "0.5px" }}>
              RECENT WORKOUTS
            </span>
          </div>

          {recentWorkouts.length > 0 ? (
            recentWorkouts.map((workout, index) => (
          <div
            key={index}
            className="p-4 mb-3 relative overflow-hidden"
            style={{
              backgroundColor: "#27272A",
              borderRadius: "16px",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: "#FF6B35" }}></div>

            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span style={{ color: "#F0F0F0", fontSize: "16px", fontWeight: 700 }}>
                    {workout.name}
                  </span>
                  {workout.isNew && (
                    <span
                      className="px-2 py-0.5 rounded"
                      style={{ backgroundColor: "#166534", color: "#4ADE80", fontSize: "10px", fontWeight: 700 }}
                    >
                      NEW
                    </span>
                  )}
                </div>
                <span style={{ color: "#FFA033", fontSize: "13px", fontWeight: 500 }}>
                  {workout.focus}
                </span>
              </div>
              <ChevronRight size={20} style={{ color: "#71717A" }} />
            </div>

            <div className="flex items-center gap-6 mb-3">
              <div>
                <div className="flex items-baseline gap-1">
                  <span style={{ color: "#F0F0F0", fontSize: "18px", fontWeight: 700 }}>{workout.sets}</span>
                  {workout.hasPR && <span style={{ color: "#4ADE80", fontSize: "13px", fontWeight: 600 }}>{workout.delta}</span>}
                </div>
                <span style={{ color: "#71717A", fontSize: "11px" }}>Sets</span>
              </div>
              <div>
                <div className="flex items-baseline gap-1">
                  <span style={{ color: "#F0F0F0", fontSize: "18px", fontWeight: 700 }}>{workout.volume}</span>
                  {!workout.hasPR && <span style={{ color: "#4ADE80", fontSize: "13px", fontWeight: 600 }}>{workout.delta}</span>}
                </div>
                <span style={{ color: "#71717A", fontSize: "11px" }}>Volume</span>
              </div>
              <div>
                <span
                  style={{
                    color: workout.intensity === "HARD" ? "#EF4444" : "#71717A",
                    fontSize: "13px",
                    fontWeight: 700,
                  }}
                >
                  {workout.intensity}
                </span>
                <div style={{ color: "#71717A", fontSize: "11px" }}>Intensity</div>
              </div>
            </div>

            <div className="h-16" style={{ backgroundColor: "#1F2228", borderRadius: "6px", overflow: "hidden" }}>
              <div className="h-full flex items-end px-2 gap-1">
                {workout.bars.length > 0 ? (
                  workout.bars.map((barHeight, i) => (
                    <div
                      key={i}
                      className="flex-1"
                      style={{
                        height: `${barHeight}%`,
                        backgroundColor: index === 0 ? "#FF6B35" : "#FFA033",
                        opacity: 0.8,
                      }}
                    ></div>
                  ))
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span style={{ color: "#71717A", fontSize: "11px" }}>No set data</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))
          ) : (
            <div
              className="p-4 mb-3"
              style={{
                backgroundColor: "#27272A",
                borderRadius: "16px",
                border: "1px solid rgba(255,255,255,0.06)",
                textAlign: "center",
              }}
            >
              <p style={{ color: "#71717A", fontSize: "13px" }}>No workouts tracked yet this week</p>
            </div>
          )}
        </>
      )}

      {/* Training Insights */}
      <div
        className="p-4"
        style={{
          backgroundColor: "#27272A",
          borderRadius: "16px",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <span style={{ color: "#F0F0F0", fontSize: "15px", fontWeight: 600 }}>
            TRAINING INSIGHTS
          </span>
          <button style={{ color: "#71717A", fontSize: "13px" }}>Expand</button>
        </div>

        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <span style={{ color: "#F0F0F0", fontSize: "28px", fontWeight: 700 }}>
              {consistency.score}%
            </span>
            <span style={{ color: "#71717A", fontSize: "12px" }}>
              {consistency.plannedPerWeek > 0
                ? `${Math.min(4, Math.floor(consistency.completed / consistency.plannedPerWeek))} weeks`
                : "N/A"}
            </span>
          </div>
          <span style={{ color: "#71717A", fontSize: "13px" }}>Consistency Score</span>
          <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "#3F3F46" }}>
            <div
              className="h-full rounded-full"
              style={{ width: `${consistency.score}%`, backgroundColor: "#FF6B35" }}
            ></div>
          </div>
        </div>

        <div className="h-32 rounded-lg overflow-hidden" style={{ backgroundColor: "#1F2228" }}>
          <div className="h-full flex items-end px-2">
            {volumeBars.length > 0 ? (
              volumeBars.map((barHeight, i) => (
                <div
                  key={i}
                  className="flex-1"
                  style={{
                    height: `${barHeight}%`,
                    backgroundColor: "#EF4444",
                    opacity: 0.7,
                    marginRight: "1px",
                  }}
                ></div>
              ))
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span style={{ color: "#71717A", fontSize: "11px" }}>No volume data yet</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
